import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, Copy, Edit, Eye, Filter, Mail, Plus, Search, Trash2, X } from 'lucide-react';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { Pagination } from '../../components/ui/Pagination';
import { Tooltip } from '../../components/ui/Tooltip';
import { ColumnCustomizer } from '../../components/ColumnCustomizer';
import { PermissionGate } from '../../components/PermissionGate';
import { PracticeDetailModal, practiceStatusVariant } from '../../components/training/practice/PracticeDetailModal';
import { TrainingEmailModal } from '../../components/training/TrainingEmailModal';
import { attendeeCount, practiceTotal } from '../../utils/practices';
import { formatEuro } from '../../utils/currency';
import { recipientGroups, sendPracticeEmail } from '../../utils/practiceEmail';
import { useFilters } from '../../hooks/useFilters';
import { useSearch } from '../../hooks/useSearch';
import { usePagination } from '../../hooks/usePagination';
import { usePractices } from '../../hooks/usePractices';
import { practicesStore } from '../../services/practicesStore';
import { useTrainingBilling } from '../../hooks/useTrainingBilling';
import { trainingCategories } from '../../data/trainingProducts';
import { practiceStatuses } from '../../data/practiceOptions';
import { requestedCourseLabel, requesterKindLabel, requesterName } from '../../utils/practices';
import { getTrainingProduct } from '../../utils/trainingCourses';
import { Practice, PracticeStatus } from '../../types/practice';
const columnsConfig = [{
  key: 'requester',
  label: 'Richiedente'
}, {
  key: 'course',
  label: 'Corso richiesto'
}, {
  key: 'participants',
  label: 'N° partecipanti'
}, {
  key: 'assigned',
  label: 'Corso Assegnato'
}, {
  key: 'amount',
  label: 'Importo'
}, {
  key: 'status',
  label: 'Stato pratica'
}, {
  key: 'billing',
  label: 'Fatturazione'
}, {
  key: 'certificates',
  label: 'Attestati'
}, {
  key: 'actions',
  label: 'Azioni'
}];
const rightAligned = ['amount'];
type SavedState = {
  saved?: 'created' | 'updated';
  id?: string;
} | null;
const statusOrder: Record<PracticeStatus, number> = {
  'In valutazione': 0,
  Positivo: 1,
  Negativo: 2
};
export function PracticesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    practices
  } = usePractices();
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [viewing, setViewing] = useState<Practice | null>(null);
  const [deleting, setDeleting] = useState<Practice | null>(null);
  const [emailing, setEmailing] = useState<Practice | null>(null);
  const [banner, setBanner] = useState((location.state as SavedState)?.saved);
  useEffect(() => {
    if (!banner) return;
    const timer = setTimeout(() => setBanner(undefined), 4000);
    return () => clearTimeout(timer);
  }, [banner]);

  // Searchable/filterable view of each practice, grouped by status:
  // In valutazione first, then Positivo, then Negativo (stable within each group)
  const rows = useMemo(() => practices.map(p => ({
    ...p,
    requester: requesterName(p),
    course: requestedCourseLabel(p)
  })).sort((a, b) => statusOrder[a.status] - statusOrder[b.status]), [practices]);
  // Highlighted rows: invoice paid but certificates still to be sent
  const billing = useTrainingBilling();
  const billingByPractice = useMemo(() => new Map(billing.map(b => [b.practiceId, b.status])), [billing]);
  const certificatesToSend = useMemo(() => {
    const paid = new Set(billing.filter(b => b.status === 'Fattura pagata').map(b => b.practiceId));
    return new Set(practices.filter(p => paid.has(p.id) && p.certificates === 'Da inviare').map(p => p.id));
  }, [billing, practices]);
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData,
    activeFilterCount
  } = useFilters(rows);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filteredData, ['requester', 'course', 'productCode', 'courseCode']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const show = (key: string) => visibleColumns.includes(key);
  const shownColumns = columnsConfig.filter(c => show(c.key));
  const selected = practices.find(p => p.id === selectedId);
  const openEdit = (p: Practice) => navigate(`/training/pratiche/${p.id}/modifica`);
  return <Layout data-id="element-6331" data-mp="1zw3ios1cnrg9">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-6332" data-mp="1zwd92gqb40mh">
        <div data-id="element-6333" data-mp="24tlucq6r4kry">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-6334" data-mp="24tc5x71culod">Pratiche</h1>
          <p className="text-slate-500 mt-1" data-id="element-6335" data-mp="177ofr3vhszla">
            Iscrizioni ai corsi di gruppi di lavoratori o singoli individui.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-6336" data-mp="1h4kh8pmz4lgc">
          <PermissionGate module="training" action="create" data-id="element-6337" data-mp="1h3hpkfhtuezd">
            <Tooltip content={selected ? `Duplica la pratica di ${requesterName(selected)}` : 'Seleziona una pratica nella tabella'} data-id="element-6338" data-mp="1l65y0m2mpjvt">
              <Button variant="flat" color="secondary" startContent={<Copy className="h-4 w-4" data-id="element-6340" data-mp="xfi5hqes899d" />} disabled={!selected} onClick={() => selected && navigate(`/training/pratiche/nuova?duplica=${selected.id}`)} data-id="element-6339" data-mp="2wkjidgy6bh73">
                Duplica pratica
              </Button>
            </Tooltip>
            <Button onClick={() => navigate('/training/pratiche/nuova')} startContent={<Plus className="h-4 w-4" data-id="element-6342" data-mp="dstve6id9tqu" />} data-id="element-6341" data-mp="11ixy8bnqdpgs">
              Nuova pratica
            </Button>
          </PermissionGate>
        </div>
      </div>

      <AnimatePresence data-id="element-6343" data-mp="nin6z7iie8v3">
        {banner && <motion.div initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} role="status" className="mb-6 flex items-center gap-3 rounded-xl bg-green-50 border border-green-200 px-4 py-3" data-id="element-6344" data-mp="nkgf6r8rt157">
            <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" data-id="element-6345" data-mp="21pa3ep9j3kse" />
            <p className="flex-1 text-sm font-medium text-green-700" data-id="element-6346" data-mp="26hl59xntxgnl">
              {banner === 'created' ? 'Pratica creata con successo' : 'Pratica modificata con successo'}
            </p>
            <button type="button" aria-label="Chiudi messaggio" onClick={() => setBanner(undefined)} className="text-green-600 hover:text-green-800" data-id="element-6347" data-mp="1xpsxjuq6wlmp">
              <X className="h-4 w-4" data-id="element-6348" data-mp="2pfdxo60tu86q" />
            </button>
          </motion.div>}
      </AnimatePresence>

      <Card className="mb-6" noPadding data-id="element-6349" data-mp="2bi8ucdshznf6">
        <div className="p-4 flex flex-col gap-4" data-id="element-6350" data-mp="2geprtyll7e4x">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-6351" data-mp="2gdefvbhzn02q">
            <div className="flex-1" data-id="element-6352" data-mp="100pt2qyh9uj9">
              <Input placeholder="Cerca richiedente, corso, codice prodotto o codice corso..." startContent={<Search className="h-4 w-4" data-id="element-6354" data-mp="1wnnfownygehp" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-6353" data-mp="zzo89urnttoc" />
            </div>
            <div className="flex gap-2" data-id="element-6355" data-mp="1mph9w3z12o7k">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-6357" data-mp="2fd3f1gnwm0wj" />} data-id="element-6356" data-mp="1mq8wwqek7dm8">
                Filtri{' '}
                {activeFilterCount > 0 && <Badge variant="primary" size="sm" className="ml-1" data-id="element-6358" data-mp="2ah4xod0ztj5m">
                    {activeFilterCount}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="training-practices" lockedColumns={['requester']} data-id="element-6359" data-mp="20oh2fmpnvn90" />
            </div>
          </div>
          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100" data-id="element-6360" data-mp="31owmvx8m7iwp">
              <Select label="Stato della pratica" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, ...practiceStatuses.map(s => ({
            value: s,
            label: s
          }))]} data-id="element-6361" data-mp="3ani33lqm6lub" />
              <Select label="Macrocategoria" value={filters.category || ''} onChange={e => setFilter('category', e.target.value)} options={[{
            value: '',
            label: 'Tutte'
          }, ...trainingCategories.map(c => ({
            value: c,
            label: c
          }))]} data-id="element-6362" data-mp="3okwyr87gnpuq" />
              <Select label="Stato del corso" value={filters.courseStatus || ''} onChange={e => setFilter('courseStatus', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'Programmato',
            label: 'Programmato'
          }, {
            value: 'Eseguito',
            label: 'Eseguito'
          }, {
            value: 'On-demand',
            label: 'On-demand'
          }]} data-id="element-6363" data-mp="c6e777fkd8x7" />
              <Select label="Attestati" value={filters.certificates || ''} onChange={e => setFilter('certificates', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'Da inviare',
            label: 'Da inviare'
          }, {
            value: 'Inviati',
            label: 'Inviati'
          }]} data-id="element-6364" data-mp="3pgug4sdw961i" />
              <div className="col-span-full flex justify-end" data-id="element-6365" data-mp="245uko0ftrw1e">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-6367" data-mp="21lxxcpqd90dv" />} data-id="element-6366" data-mp="2934b9jeyadqt">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Card noPadding className="overflow-hidden" data-id="element-6368" data-mp="1rrz0a555g6ss">
        <div className="overflow-x-auto" data-id="element-6369" data-mp="1mw9wtqq0e5r7">
          <table className="w-full text-left border-collapse table-auto" data-id="element-6370" data-mp="1mt5pikh8ytwb">
            <thead data-id="element-6371" data-mp="2kih2z51yo65o">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-6372" data-mp="2kgwgm2psvtiw">
                <th className="pl-6 pr-2 py-4 w-10" data-id="element-6373" data-mp="2ak9yhlouzgkk">
                  <span className="sr-only" data-id="element-6374" data-mp="2aj00sxlx4aeg">Seleziona</span>
                </th>
                {shownColumns.map(col => <th key={col.key} className={`px-4 py-4 whitespace-nowrap ${rightAligned.includes(col.key) ? 'text-right' : ''} ${col.key === 'participants' ? 'text-center' : ''} ${col.key === 'actions' ? 'pr-6 text-right' : ''}`} data-id="element-6375" data-mp="z5o2t3cuam07">
                    {col.label}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-6376" data-mp="mnak8jhespkt">
              {paginatedData.length === 0 && <tr data-id="element-6377" data-mp="rlvcnwqf64yp">
                  <td colSpan={shownColumns.length + 1} className="px-4 py-16 text-center text-sm text-slate-500" data-id="element-6378" data-mp="rl3pnaaw1fk1">
                    {practices.length === 0 ? 'Nessuna pratica inserita.' : 'Nessuna pratica corrisponde alla ricerca o ai filtri.'}
                  </td>
                </tr>}
              {paginatedData.map(p => {
              const isSelected = selectedId === p.id;
              const product = getTrainingProduct(p.productCode);
              const toSend = certificatesToSend.has(p.id);
              const amount = practiceTotal(p);
              const billingStatus = billingByPractice.get(p.id);
              return <tr key={p.id} className={`transition-colors ${isSelected ? 'bg-orange-50/60' : toSend ? 'bg-yellow-100 hover:bg-yellow-200/70' : 'hover:bg-slate-50/80'}`} title={toSend ? 'Fattura pagata: attestati da inviare' : undefined} data-id="element-6379" data-mp="37fqffflusj81">
                    <td className="pl-6 pr-2 py-4" data-id="element-6380" data-mp="3qc8buabnd7am">
                      <input type="checkbox" aria-label={`Seleziona pratica di ${p.requester}`} className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={isSelected} onChange={() => setSelectedId(isSelected ? null : p.id)} data-id="element-6381" data-mp="7za6tozzzo9l" />
                    </td>
                    {show('requester') && <td className="px-4 py-4" data-id="element-6382" data-mp="112w6hg6gxowe">
                        <p className="text-sm font-medium text-seform-navy" data-id="element-6383" data-mp="111l2pgd0wh5a">
                          {p.requester}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5" data-id="element-6384" data-mp="1asxsm2f9gx8o">
                          {requesterKindLabel(p)}
                        </p>
                        {toSend && <p className="text-xs font-medium text-yellow-800 mt-1" data-id="element-6385" data-mp="e51cszrne5yi">
                            Fattura pagata · Attestati da inviare
                          </p>}
                      </td>}
                    {show('course') && <td className="px-4 py-4" data-id="element-6386" data-mp="s03dn8hn154g">
                        <p className="text-sm text-slate-700" data-id="element-6387" data-mp="1yhgrjrcf7xo3">{p.course}</p>
                        <p className="text-xs text-slate-400 mt-0.5" data-id="element-6388" data-mp="23hcz20kbu78e">
                          {product?.type && `${product.type} · `}
                          <span className="font-mono" data-id="element-6389" data-mp="3nycd9q0n4jhe">{p.productCode}</span>
                        </p>
                        {p.requestedCourseCode && <p className="text-xs text-slate-400 mt-0.5" data-id="element-6390" data-mp="3e9kplu2m4lca">
                            Richiesto:{' '}
                            <span className="font-mono" data-id="element-6391" data-mp="39bj6j956sm0t">{p.requestedCourseCode}</span>
                          </p>}
                      </td>}
                    {show('participants') && <td className="px-4 py-4 text-sm text-slate-700 text-center tabular-nums" data-id="element-6392" data-mp="308i2rf3sn70b">
                        {attendeeCount(p) ?? '-'}
                      </td>}
                    {show('assigned') && <td className="px-4 py-4 text-sm font-mono text-slate-700 whitespace-nowrap" data-id="element-6393" data-mp="tjd55ttcx8z0">
                        {p.courseCode || <span className="text-slate-400" data-id="element-6394" data-mp="ok0gr4mu7tub">—</span>}
                      </td>}
                    {show('amount') && <td className="px-4 py-4 text-sm text-slate-700 text-right tabular-nums whitespace-nowrap" data-id="element-6395" data-mp="2iynuryfm63sc">
                        {amount !== undefined ? formatEuro(amount) : '—'}
                      </td>}
                    {show('status') && <td className="px-4 py-4" data-id="element-6396" data-mp="2e274axkxzdc3">
                        <Badge variant={practiceStatusVariant[p.status]} size="sm" data-id="element-6397" data-mp="31ri163z68nky">
                          {p.status}
                        </Badge>
                      </td>}
                    {show('billing') && <td className="px-4 py-4 text-sm text-slate-700 whitespace-nowrap" data-id="element-6398" data-mp="2sq0lgmpoenws">
                        {billingStatus || <span className="text-slate-400" data-id="element-6399" data-mp="2wqc5pye4mm7q">—</span>}
                      </td>}
                    {show('certificates') && <td className="px-4 py-4" data-id="element-6400" data-mp="1ld8lrzcwq969">
                        <Badge variant={p.certificates === 'Inviati' ? 'success' : 'neutral'} size="sm" data-id="element-6401" data-mp="1lavw100wquin">
                          {p.certificates}
                        </Badge>
                      </td>}
                    {show('actions') && <td className="px-4 py-4 pr-6" data-id="element-6402" data-mp="1isao6me9av22">
                        <div className="flex justify-end gap-1" data-id="element-6403" data-mp="1iqpror8dt21k">
                          <Tooltip content="Vedi" data-id="element-6404" data-mp="v3rbluo6bw46">
                            <Button variant="light" size="sm" className="!px-1" aria-label="Vedi pratica" onClick={() => setViewing(p)} data-id="element-6405" data-mp="q4513szqctz2">
                              <Eye className="h-4 w-4 text-slate-500" data-id="element-6406" data-mp="cbyo2frvj33u" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Invia email" data-id="element-6407" data-mp="7d56oc9gurye">
                            <Button variant="light" size="sm" className="!px-1" aria-label="Invia email al cliente" onClick={() => setEmailing(p)} data-id="element-6408" data-mp="3i09mv7tdwaa6">
                              <Mail className="h-4 w-4 text-slate-500" data-id="element-6409" data-mp="3w0ssy9eah69p" />
                            </Button>
                          </Tooltip>
                          <PermissionGate module="training" action="edit" data-id="element-6410" data-mp="9s19nn56r0f2">
                            <Tooltip content="Modifica" data-id="element-6411" data-mp="9py9496epow3">
                              <Button variant="light" size="sm" className="!px-1" aria-label="Modifica pratica" onClick={() => openEdit(p)} data-id="element-6412" data-mp="ejsqv72gbtsj">
                                <Edit className="h-4 w-4 text-slate-500" data-id="element-6413" data-mp="sisfiepys8op" />
                              </Button>
                            </Tooltip>
                          </PermissionGate>
                          <PermissionGate module="training" action="delete" data-id="element-6414" data-mp="2805hny26raqh">
                            <Tooltip content="Elimina" data-id="element-6415" data-mp="240wrf36ctuzo">
                              <Button variant="light" size="sm" className="!px-1" aria-label="Elimina pratica" onClick={() => setDeleting(p)} data-id="element-6416" data-mp="1z0s8z7lvyq6t">
                                <Trash2 className="h-4 w-4 text-red-500" data-id="element-6417" data-mp="2mxpicss1ujqu" />
                              </Button>
                            </Tooltip>
                          </PermissionGate>
                        </div>
                      </td>}
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100" data-id="element-6418" data-mp="3sbah1jv0nd8n">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-6419" data-mp="3sd4y2n362l7g" />
        </div>
      </Card>

      <PracticeDetailModal practice={viewing} onClose={() => setViewing(null)} onEdit={openEdit} onEmail={p => {
      setViewing(null);
      setEmailing(p);
    }} data-id="element-6420" data-mp="3dfl0w2cihubu" />
      <TrainingEmailModal isOpen={Boolean(emailing)} title="Email al cliente" context={emailing ? requestedCourseLabel(emailing) : undefined} groups={emailing ? recipientGroups([emailing]) : []} onClose={() => setEmailing(null)} onSend={body => emailing && sendPracticeEmail([emailing], body)} data-id="element-6421" data-mp="9yclrq9658kt" />
      <Modal isOpen={Boolean(deleting)} onClose={() => setDeleting(null)} title="Elimina pratica" size="md" data-id="element-6422" data-mp="svx0i4i7yqrp">
        {deleting && <>
            <p className="text-sm text-slate-600" data-id="element-6423" data-mp="156eiirk8e6sv">
              Vuoi eliminare la pratica di{' '}
              <span className="font-semibold text-slate-800" data-id="element-6424" data-mp="1a60ukxobph03">
                {requesterName(deleting)}
              </span>
              ? L'operazione non può essere annullata.
            </p>
            <div className="flex justify-end gap-3 pt-6" data-id="element-6425" data-mp="1k46wvge1kp5h">
              <Button variant="flat" color="default" onClick={() => setDeleting(null)} data-id="element-6426" data-mp="1k3ornu9ej0lf">
                Annulla
              </Button>
              <Button color="danger" onClick={() => {
            practicesStore.remove(deleting.id);
            if (selectedId === deleting.id) setSelectedId(null);
            setDeleting(null);
          }} data-id="element-6427" data-mp="1sw8s90997f8w">
                Elimina
              </Button>
            </div>
          </>}
      </Modal>
    </Layout>;
}